<template>
    <div style="flex-grow: 1; display: flex; flex-direction: column;">
        <FlowV2ListToolbar v-model="searchText">
            <QBtn flat round dense icon="mdi-plus" color="blue-grey-8" :disable="readOnly" @click="addNewVariable">
                <QTooltip>Add</QTooltip>
            </QBtn>
            <span>
                <QBtn flat round dense icon="mdi-trash-can-outline" color="blue-grey-8" @click="removeVariables" :disable="readOnly || isDeleteDisabled"></QBtn>
                <QTooltip>Delete</QTooltip>
            </span>
        </FlowV2ListToolbar>
        <AgGridVue
                   style="flex-grow: 1; display: flex; flex-direction: column;"
                   v-model="rowData"
                   :columnTypes="columnTypes"
                   :columnDefs="columnDefs"
                   :headerHeight="30"
                   :rowHeight="33"
                   :quickFilterText="searchText"
                   :rowSelection="rowSelection"
                   :getRowStyle="getRowStyle"
                   @grid-ready="gridReady"
                   @selection-changed="rowSelectionChanged"
                   @cell-value-changed="cellValueChanged"
        >

        </AgGridVue>
    </div>
</template>

<script>
    import { QBtn, QTooltip } from 'quasar';
    import { AgGridVue } from "ag-grid-vue3";
    import FlowV2ListToolbar from '../browser/flowv2ListToolbar.jsx';

    const KIND_META = {
        media: { icon: 'mdi-access-point', label: 'Media' },
        globals: { icon: 'mdi-earth', label: 'Globals' },
        parameter: { icon: 'mdi-import', label: 'Parameter' },
        code: { icon: 'mdi-code-braces', label: 'Defined in code' },
        variable: { icon: 'mdi-variable', label: 'Variable' },
    };

    function kindMeta(kind) {
        return KIND_META[kind] || KIND_META.variable;
    }

    function renderNameCell(params) {
        const meta = kindMeta(params.data?.kind);
        const wrap = document.createElement('span');
        wrap.style.cssText = 'display:inline-flex;align-items:center;gap:6px;height:100%;min-width:0;max-width:100%;';
        const icon = document.createElement('i');
        icon.className = `mdi ${meta.icon}`;
        icon.title = params.data?.locked ? `${meta.label} (read-only)` : meta.label;
        icon.style.cssText = 'font-size:16px;line-height:1;flex:0 0 auto;';
        const text = document.createElement('span');
        text.textContent = params.value == null ? '' : String(params.value);
        text.style.cssText = 'overflow:hidden;text-overflow:ellipsis;';
        wrap.appendChild(icon);
        wrap.appendChild(text);
        return wrap;
    }

    /** Grid-only fields stay off the local that is saved. */
    function localPayload(data, name) {
        const payload = {
            Name: name != null ? name : data?.Name,
            Type: data?.Type,
            Persistent: !!data?.Persistent,
        };
        if (data?.Description != null)
            payload.Description = data.Description;
        return payload;
    }

    export default {
        name: 'flow-v2-variables',
        components: {
            QBtn, QTooltip,
            AgGridVue,
            FlowV2ListToolbar,

        },
        emits: ['addvar','removevar','updatevar'],
        props: {
            items: null,
            type: null,
            readOnly: { type: Boolean, default: false },
        },
        data() {
            return {
                gridApi: null,
                columnApi: null,

                searchText: '',
                rowData: [],
                columnDefs: [],
                columnTypes: {
                    variableType: {
                        sortable: true,
                        resizable: true,
                        enablePivot: true,
                        filter: 'agSetColumnFilter',
                        filterParams: { values: ['string', 'int', 'bool', 'float', 'dobule', 'DateTime', 'json'] },
                        cellEditor: 'agSelectCellEditor',
                        cellEditorParams: {
                            values: ['string', 'int', 'bool', 'float', 'dobule', 'DateTime', 'json'],
                        }
                    },
                    booleanType: {
                        sortable: true,
                        resizable: true,
                        cellDataType: 'boolean',
                        filter: 'agSetColumnFilter',
                        filterParams: { values: [true, false] },
                    },
                },

                selectedRowsData: [],
                rowSelection: {
                    mode: 'multiRow',
                    isRowSelectable: (node) => !node?.data?.locked,
                    hideDisabledCheckboxes: true,
                },
            }
        },
        created() {
            if (this.items)
                this.rowData = [...this.items];

            if (this.type)
                this.createColDefs();
        },
        mounted() {

        },
        computed: {
            isDeleteDisabled() {
                return this.selectedRowsData.length < 1;
            },
        },
        watch: {
            items: {
                handler(newv) {
                    this.rowData = newv;
                    if (this.gridApi)
                        this.gridApi.setGridOption("rowData", this.rowData);

                    console.log(`Grid data changed: ${JSON.stringify(this.rowData)}`);
                },
                deep: true
            },
            type() {
                this.createColDefs();
            },
            readOnly() {
                this.createColDefs();
            },
        },
        methods: {
            gridReady(params) {
                this.gridApi = params.api;
                this.columnApi = params.columnApi;
            },
            rowEditable(params) {
                return !this.readOnly && !params?.data?.locked;
            },
            getRowStyle(params) {
                if (params?.data?.locked)
                    return { color: '#78909c' };
                return null;
            },
            createColDefs() {
                const editableCustom = (params) => this.rowEditable(params);
                const editableTyped = (params) => this.rowEditable(params) && this.type != 'APIHeader';
                this.columnDefs = [
                    {
                        field: 'Name',
                        editable: editableCustom,
                        flex: 1,
                        cellRenderer: renderNameCell,
                    },
                    {
                        field: 'Type',
                        editable: editableTyped,
                        type: 'variableType',
                        flex: 1,
                    },
                    {
                        field: 'Persistent',
                        editable: editableTyped,
                        type: 'booleanType',
                        flex: 1,
                    }
                ];
            },
            rowSelectionChanged(e) {
                const rows = this.gridApi.getSelectedNodes();

                this.selectedRowsData = rows.map(d => d.data).filter(d => d && !d.locked);
            },
            cellValueChanged(e) {
                if (this.readOnly || e.data?.locked)
                    return;
                if (e.colDef.field == 'Name') {
                    const isnew = e.data.new;
                    delete e.data.new;

                    if (!isnew)
                        this.$emit('removevar', localPayload(e.data, e.oldValue));

                    this.$emit('addvar', localPayload(e.data));
                }
                else if (e.colDef.field == 'Persistent') {
                    e.data.Type = 'json';
                    this.$emit('updatevar', localPayload(e.data));
                }
                else {
                    this.$emit('updatevar', localPayload(e.data));
                }
            },

            removeVariables() {
                if (this.readOnly)
                    return;
                const selectedRows = (this.gridApi.getSelectedRows() || []).filter(row => !row?.locked);
                if (selectedRows.length === 0) {
                    return;
                }

                this.gridApi.applyTransaction({ remove: selectedRows });

                for (let item of selectedRows)
                    this.$emit('removevar', localPayload(item));
            },
            addNewVariable() {
                if (this.readOnly)
                    return;
                const newRow = {
                    Name: 'unnamed',
                    Type: this.type == 'APIHeader' ? 'json' : 'string',
                    Persistent: this.type == 'APIHeader',
                    kind: 'variable',
                    new: true,
                };
                const result = this.gridApi.applyTransaction({ add: [newRow] });

                const newRowIndex = result.add?.[0]?.rowIndex;
                if (newRowIndex !== undefined) {
                    this.gridApi.startEditingCell({
                        rowIndex: newRowIndex,
                        colKey: 'Name',
                    });
                }
            },
        }
    };
</script>
